import cx from "classnames";
import { Location } from "history";
import React, { useEffect, useRef } from "react";

import GridItem, { GridItemPlaceholder } from "@/mk2/apps/bazaar/components/GridItem";
import GridStrollerItem from "@/mk2/apps/bazaar/components/GridStrollerItem";
import { IS_SLOW_SCROLL_ENABLED, ITEMS_PER_PAGE } from "@/mk2/apps/bazaar/constants";
import { Checked } from "@/mk2/apps/bazaar/containers/HerBazaar/HerBazaar.actions";
import { ProductEntity } from "@/mk2/apps/bazaar/schemas";
import { LoadingBar } from "@/mk2/components/LoadingBar";
import { Panel } from "@/mk2/components/Panel";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import { ComplaintEntity } from "@/mk2/containers/Complaint/Complaint.schemas";
import { StrollerEntity, StrollerPhoto, UserEntity } from "@/mk2/schemas";
import { BZ2_NO_MORE_PRODUCTS } from "@/mk/autogenerated/translations/Grid.befa20668a8943f81dd26a534fc2528c";
import { isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

import styles from "./Grid.mscss";

// indexes 0 - 15 are dedicated for strollers (if fetched), else show bz prods
const STROLLERS_FIRST_TILL_INDEX = 15;

// indexes below are special for rendering AdSlot before items with these indexes
const DESKTOP_ADSLOT_INDEXES = {
    8: "Market Section - Mid 01 - Desktop",
};
const MOBILE_ADSLOT_INDEXES = {
    6: "Market Section - Mid 01 - Mobile",
    14: "Market Section - Mid 02 - Mobile",
    22: "Market Section - Mid 03 - Mobile",
    30: "Market Section - Mid 04 - Mobile",
    38: "Market Section - Mid 05 - Mobile",
};

interface OwnProps {
    className?: string;
    products: ProductEntity[];

    strollers?: StrollerEntity[];
    strollerPhotosMap?: Record<number, StrollerPhoto>;

    requestUser: UserEntity;
    isModerator?: boolean;
    isAdmin?: boolean;
    showCreatedTime?: boolean;
    showStarredTime?: boolean;
    showStateBadges?: boolean;
    checked?: Checked;
    complaintsPerProduct?: {
        [productId: number]: ComplaintEntity[];
    };
    isMobile?: boolean;
    location: Location;
    isLoading?: boolean;

    onItemClick?(product: ProductEntity);
    onStrollerItemClick?(stroller: StrollerEntity, variantId: number, gridIndex: number);
}

type Props = OwnProps;

const Grid: React.FunctionComponent<Props> = ({
    className,
    products,

    strollers,
    strollerPhotosMap,

    requestUser,
    isModerator,
    isAdmin = false,
    isMobile = false,
    showCreatedTime = false,
    showStarredTime = false,
    showStateBadges = false,
    checked,
    complaintsPerProduct,
    location,
    isLoading = false,
    onItemClick,
    onStrollerItemClick,
}) => {
    const isSlowScroll = IS_SLOW_SCROLL_ENABLED;
    const gridRef = useRef<HTMLDivElement>(null);

    // Stop scroll when an AdSlot enters the viewport (scrolling down)
    const snappedRef = useRef(new Set<HTMLElement>());
    const isAnimatingRef = useRef(false);

    useEffect(() => {
        const grid = gridRef.current;
        if (!isSlowScroll || !grid) return;

        // Users who opt out of motion still get the ad snapped into view, but instantly.
        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        const snapBehavior: ScrollBehavior = reduceMotion ? "auto" : "smooth";

        const snapped = snappedRef.current;
        let lastScrollY = window.scrollY;
        let rafId = 0;
        // Fallback timer in case `scrollend` never fires (older Safari, interrupted anim).
        let animFallback = 0;

        const endAnimation = () => {
            window.clearTimeout(animFallback);
            isAnimatingRef.current = false;
            // Resync so the next direction check isn't skewed by the programmatic jump.
            lastScrollY = window.scrollY;
        };

        const onScroll = () => {
            // Ignore scroll events generated by our own programmatic snap.
            if (isAnimatingRef.current) return;
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                const y = window.scrollY;
                const scrollingDown = y > lastScrollY;
                lastScrollY = y;
                if (!scrollingDown) return;

                const adEls = grid.querySelectorAll<HTMLElement>("[data-ad-snap]");
                for (const el of adEls) {
                    if (snapped.has(el)) continue;
                    const rect = el.getBoundingClientRect();
                    if (rect.top >= 0 && rect.top < window.innerHeight * 0.8) {
                        snapped.add(el);
                        isAnimatingRef.current = true;
                        window.scrollTo({ top: rect.top + y - 120, behavior: snapBehavior });
                        // `scrollend` clears the flag reliably; timer is just a safety net.
                        animFallback = window.setTimeout(endAnimation, 1500);
                        break;
                    }
                }
            });
        };

        const onScrollEnd = () => {
            if (isAnimatingRef.current) endAnimation();
        };

        // Any manual input aborts the snap so the user can scroll past freely.
        const onUserInput = () => {
            if (isAnimatingRef.current) endAnimation();
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("scrollend", onScrollEnd, { passive: true });
        window.addEventListener("wheel", onUserInput, { passive: true });
        window.addEventListener("touchstart", onUserInput, { passive: true });
        window.addEventListener("keydown", onUserInput);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("scrollend", onScrollEnd);
            window.removeEventListener("wheel", onUserInput);
            window.removeEventListener("touchstart", onUserInput);
            window.removeEventListener("keydown", onUserInput);
            cancelAnimationFrame(rafId);
            window.clearTimeout(animFallback);
            snapped.clear();
        };
    }, [isSlowScroll]);

    const itemsCount = (products.length || 0) + (strollers?.length || 0);
    if (itemsCount === 0) {
        return <Panel className={styles.Grid__noResults}>{BZ2_NO_MORE_PRODUCTS}</Panel>;
    }

    let index = 0;
    let productIndex = 0;
    let strollerIndex = 0;
    let adIndex = 0;
    const items = [];

    while (index < itemsCount) {
        if (isMobile && MOBILE_ADSLOT_INDEXES[index] && itemsCount >= index + 2) {
            const adSlot = <AdSlot zone={MOBILE_ADSLOT_INDEXES[index]} location={location} />;
            items.push(
                isSlowScroll ? (
                    <div key={`ad-${adIndex}`} data-ad-snap>
                        {adSlot}
                    </div>
                ) : (
                    <React.Fragment key={`ad-${adIndex}`}>{adSlot}</React.Fragment>
                ),
            );
            adIndex += 1;
        } else if (!isMobile && DESKTOP_ADSLOT_INDEXES[index] && itemsCount >= index + 4) {
            const adSlot = (
                <AdSlot
                    zone={DESKTOP_ADSLOT_INDEXES[index]}
                    location={location}
                    marginTopWithAd={20}
                />
            );
            items.push(
                isSlowScroll ? (
                    <div key={`ad-${adIndex}`} data-ad-snap>
                        {adSlot}
                    </div>
                ) : (
                    <React.Fragment key={`ad-${adIndex}`}>{adSlot}</React.Fragment>
                ),
            );
            adIndex += 1;
        }

        if (
            strollers?.length &&
            index <= STROLLERS_FIRST_TILL_INDEX &&
            strollers.length > strollerIndex
        ) {
            const stroller = strollers[strollerIndex];
            items.push(
                <React.Fragment key={`stroller-${strollerIndex}`}>
                    <GridStrollerItem
                        stroller={stroller}
                        className={cx(
                            styles.Grid__item,
                            styles[`Grid__item--col${index % 4}`],
                            isSlowScroll && styles["Grid__item--slowScroll"],
                        )}
                        strollerPhotosMap={strollerPhotosMap}
                        gridIndex={index}
                        onClick={onStrollerItemClick}
                    />
                </React.Fragment>,
            );
            strollerIndex += 1;
        } else if (products?.length && products.length > productIndex) {
            const product = products[productIndex];
            items.push(
                <React.Fragment key={`product-${productIndex}`}>
                    <GridItem
                        product={product}
                        className={cx(
                            styles.Grid__item,
                            styles[`Grid__item--col${index % 4}`],
                            isSlowScroll && styles["Grid__item--slowScroll"],
                        )}
                        isModerator={isModerator}
                        isSeller={isAuthenticatedAs(requestUser, product.seller.user)}
                        showCreatedTime={showCreatedTime}
                        showStarredTime={showStarredTime}
                        showStateBadges={showStateBadges}
                        checked={checked[product.id]}
                        complaintsCount={
                            complaintsPerProduct[product.id]
                                ? complaintsPerProduct[product.id].length
                                : 0
                        }
                        onClick={onItemClick}
                    />
                </React.Fragment>,
            );
            productIndex += 1;
        }
        index += 1;
    }
    return (
        <div className={cx(styles.Grid, className)} ref={gridRef}>
            <LoadingBar loading={isLoading} />
            <div
                className={cx(
                    styles.Grid__products,
                    isLoading && styles["Grid__products--loading"],
                )}
            >
                {items}
            </div>
        </div>
    );
};

Grid.displayName = "Grid";

export default React.memo<Props>(Grid);

interface PlaceholderOwnProps {
    className?: string;
    productsCount?: number;
}

let GridPlaceholder: React.FunctionComponent<PlaceholderOwnProps> = ({
    className,
    productsCount = ITEMS_PER_PAGE,
}) => (
    <div className={cx(styles.Grid, className)}>
        {Array.from({ length: productsCount }, (v, index) => (
            <GridItemPlaceholder
                className={cx(styles.Grid__item, styles[`Grid__item--col${index % 4}`])}
                key={index}
            />
        ))}
    </div>
);

GridPlaceholder.displayName = "GridPlaceholder";

GridPlaceholder = React.memo<PlaceholderOwnProps>(GridPlaceholder);

export { GridPlaceholder };
